/**
 * SwappablePowerSyncFacade — Renderer
 *
 * A stable `PowerSyncFacade` whose underlying implementation can be swapped at
 * runtime without any caller (`import { db }`) noticing. On Electron the fast
 * path is the native main-process engine (ElectronPowerSyncFacade); if that
 * engine can't run in a packaged build, `database.ts` swaps in the in-renderer
 * @powersync/web client instead so the Sales page keeps working.
 *
 * It tracks every registered status/change listener (with its original
 * options) and re-attaches them to the new inner facade on swap, so live
 * subscriptions — SyncStatusChip, usePowerSyncChange, etc. — survive the
 * switch untouched.
 */

import type {
  PowerSyncFacade,
  PowerSyncTransactionFacade,
} from './facadeTypes';

interface StatusReg {
  listener: { statusChanged?: (s: PowerSyncFacade['currentStatus']) => void };
  detach: () => void;
}

interface ChangeReg {
  listener: { onChange: (event: { changedTables: string[] }) => void };
  options?: unknown;
  detach: () => void;
}

export class SwappablePowerSyncFacade implements PowerSyncFacade {
  private inner: PowerSyncFacade;

  private statusRegs = new Set<StatusReg>();

  private changeRegs = new Set<ChangeReg>();

  constructor(initial: PowerSyncFacade) {
    this.inner = initial;
  }

  /** Replace the underlying facade, re-attaching all live listeners to it. */
  swap(next: PowerSyncFacade): void {
    if (next === this.inner) return;
    this.statusRegs.forEach((reg) => reg.detach());
    this.changeRegs.forEach((reg) => reg.detach());
    this.inner = next;
    this.statusRegs.forEach((reg) => {
      reg.detach = this.inner.registerListener(reg.listener);
    });
    this.changeRegs.forEach((reg) => {
      reg.detach = this.inner.onChange(reg.listener, reg.options);
    });
    // Push the new engine's current status to every subscriber immediately so
    // the UI (SyncStatusChip, banners) reflects the swap without waiting for
    // the next status event.
    const status = this.inner.currentStatus;
    this.statusRegs.forEach((reg) => reg.listener.statusChanged?.(status));
  }

  get currentStatus() {
    return this.inner.currentStatus;
  }

  execute(sql: string, params?: unknown[]) {
    return this.inner.execute(sql, params);
  }

  get<T = Record<string, unknown>>(sql: string, params?: unknown[]) {
    return this.inner.get<T>(sql, params);
  }

  getAll<T = Record<string, unknown>>(sql: string, params?: unknown[]) {
    return this.inner.getAll<T>(sql, params);
  }

  getUploadQueueStats() {
    return this.inner.getUploadQueueStats();
  }

  writeTransaction<T>(
    callback: (tx: PowerSyncTransactionFacade) => Promise<T>,
  ) {
    return this.inner.writeTransaction(callback);
  }

  registerListener(listener: {
    statusChanged?: (s: PowerSyncFacade['currentStatus']) => void;
  }): () => void {
    const reg: StatusReg = {
      listener,
      detach: this.inner.registerListener(listener),
    };
    this.statusRegs.add(reg);
    return () => {
      reg.detach();
      this.statusRegs.delete(reg);
    };
  }

  onChange(
    listener: { onChange: (event: { changedTables: string[] }) => void },
    options?: unknown,
  ): () => void {
    const reg: ChangeReg = {
      listener,
      options,
      detach: this.inner.onChange(listener, options),
    };
    this.changeRegs.add(reg);
    return () => {
      reg.detach();
      this.changeRegs.delete(reg);
    };
  }

  connect(connector?: unknown): Promise<void> {
    return this.inner.connect(connector);
  }
}
